import React from "react";
import { ICreateBudget } from "../../../@types";
import { createBudget, updateBudget } from "../../../backend-services";
import { validateBudgetForm } from "../../../modules/settings/settings";
import { clearFieldError, handleErrors } from "../../../utils/handleErrors";
import { Button } from "../../common";
import { DropdownComponent, InputComponent } from "../../core/Forms";
import ErrorComponent from "../../Error";
import { addCommasToNumber } from "../../../utils/convertToFloat";
import { getMonthLabel } from "../../../utils/DateTimeRange";
import { initialBudgetState } from "../../../constants";
import { useBudgetStore } from "../../../state/useBudgetStore";

interface CreateBudgetProps {
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  isLoading: boolean;
  budgetData?: ICreateBudget;
}

export const CreateBudget: React.FC<CreateBudgetProps> = ({
  onSubmit,
  isLoading,
  budgetData,
}) => {
  const { updateExistingBudget } = useBudgetStore();
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const currentMonthDropDown = [
    {
      value: `${currentMonth.toString().padStart(2, "0")}-${currentYear}`,
      label: getMonthLabel(currentMonth),
    },
  ];
  const [selectedValue, setSelectedValue] = React.useState("");
  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const [, setIsLoading] = React.useState(false);
  const [, setErrorMessage] = React.useState<string>("");
  const [formData, setFormData] = React.useState(initialBudgetState);
  const [validationResults, setValidationResults] = React.useState({
    monthYear: "",
    allocatedBudget: null,
  });

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    clearFieldError(name, validationResults, setValidationResults);
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const hasErrors = validateBudgetForm(formData, setValidationResults);
      if (!hasErrors) {
        const data = {
          ...formData,
          allocatedBudget: Number(formData.allocatedBudget),
        };
        await updateExistingBudget(data);

        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    if (budgetData) {
      setFormData(budgetData);
    }
  }, [budgetData]);

  const handleDropDownChange = (name: string, selectedValue: string) => {
    const [month, year] = selectedValue.split("-");
    const formattedValue = `${month.padStart(2, "0")}-${year}`;

    setFormData({ ...formData, monthYear: formattedValue });
    clearFieldError(name, validationResults, setValidationResults);
  };

  return (
    <div>
      <form onSubmit={onSubmit} className="max-w-2xl">
        <div className="w-full min-h-screen lg:h-screen">
          <div className="mt-4 space-y-4 md:mt-8 md:space-y-6">
            <div>
              <div className="text-[14px] md:text-[16px] mb-2">Amount</div>
              <InputComponent
                placeholder="Amount"
                name="allocatedBudget"
                type="number"
                pattern="[0-9]*"
                value={formData.allocatedBudget}
                handleChange={handleInputChange}
                className="w-full"
                error={validationResults.allocatedBudget}
              />
            </div>
            <div className="w-full">
              <div className="text-[14px] md:text-[16px] text-black mb-2">
                Month
              </div>
              <InputComponent
                placeholder="Select Month"
                name="month"
                type="text"
                value={currentMonthDropDown[0].label}
                disabled
                className="w-full"
                error={validationResults.allocatedBudget}
              />
            </div>
          </div>

          <div className="flex gap-4 mt-8">
            <Button
              text="Save Changes"
              size="full"
              font="semibold"
              variant="secondary"
              onClick={handleSubmit}
              disabled={isLoading}
              isLoading={isLoading}
            />
          </div>
        </div>
      </form>
    </div>
  );
};
